import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag } from '@carbon/react';
import { TrashCan, Add, Subtract, ArrowRight, ArrowLeft, ShoppingCart } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { toWebpImageUrl } from '../utils/imageUrl';

const CartPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { cartItems, cartTotal, updateQuantity, removeFromCart } = useCart();

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const subtotal = cartTotal;
  const shipping = subtotal > 200 ? 0 : 9.99;
  const tax = subtotal * 0.10; // GST 10%
  const total = subtotal + shipping + tax;

  // Format category name for display - match ShopPage format
  const formatCategoryName = (category) => {
    // Debug log
    console.log('Cart item category:', category, typeof category);
    
    if (!category || category === 'undefined' || category === 'null' || category === null) {
      // Default to Sensor instead of General
      return 'Sensor';
    }
    // Map database values to display names
    const categoryMap = {
      'sensor': 'Sensor',
      'gateway': 'Gateway',
      'bundle': 'Bundle',
      'probes': 'Probe',
      'probe': 'Probe',
      'all': 'All'
    };
    const lowerCategory = String(category).toLowerCase().trim();
    const formatted = categoryMap[lowerCategory] || category.charAt(0).toUpperCase() + category.slice(1);
    console.log('Formatted category:', formatted);
    return formatted;
  };

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="cart-page">
      {/* Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '16px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ArrowLeft}
              onClick={() => navigate('/shop')}
              style={{ marginBottom: '12px' }}
              data-testid="continue-shopping"
            >
              Continue Shopping
            </Button>
            <h1 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
              Shopping Cart
            </h1>
            <p style={{ color: textSecondary, fontSize: '0.875rem', marginTop: '4px' }}>
              {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
            </p>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 0' }}>
        <Grid>
          {cartItems.length === 0 ? (
            <Column lg={16} md={8} sm={4}>
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '48px 24px', textAlign: 'center' }}>
                <ShoppingCart size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', marginBottom: '12px' }}>Your cart is empty</h2>
                <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '0.875rem' }}>Add some products to get started</p>
                <Button kind="primary" onClick={() => navigate('/shop')} data-testid="shop-now">
                  Shop Now
                </Button>
              </Tile>
            </Column>
          ) : (
            <>
              {/* Cart Items */}
              <Column lg={11} md={8} sm={4}>
                {cartItems.map((item) => (
                  <Tile
                    key={item.id}
                    style={{
                      backgroundColor: bgSecondary,
                      border: `1px solid ${borderColor}`,
                      padding: '0',
                      marginBottom: '12px',
                      overflow: 'hidden',
                    }}
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Mobile Card Layout */}
                    <div style={{ padding: '16px' }}>
                      {/* Product Info Row */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <img
                          src={toWebpImageUrl(item.image)}
                          alt={item.name}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'contain',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            padding: '8px',
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Tag 
                            type="blue" 
                            size="sm" 
                            style={{ 
                              marginBottom: '6px',
                              color: '#ffffff',
                              display: 'inline-block'
                            }}
                          >
                            {formatCategoryName(item.category || 'sensor')}
                          </Tag>
                          <h3 style={{
                            color: textPrimary,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            margin: 0,
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {item.name}
                          </h3>
                          <p style={{ color: '#0f62fe', fontSize: '1.125rem', fontWeight: 600, margin: '8px 0 0' }}>
                            ${item.price.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Quantity and Actions Row */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '12px',
                        borderTop: `1px solid ${borderColor}`,
                      }}>
                        {/* Quantity Controls */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: bgPrimary,
                          borderRadius: '8px',
                          padding: '4px',
                        }}>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Subtract}
                            iconDescription="Decrease"
                            onClick={() => updateQuantity(item.id, -1)}
                            data-testid={`decrease-${item.id}`}
                          />
                          <span style={{
                            color: textPrimary,
                            minWidth: '32px',
                            textAlign: 'center',
                            fontSize: '1rem',
                            fontWeight: 600,
                          }}>
                            {item.quantity}
                          </span>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={Add}
                            iconDescription="Increase"
                            onClick={() => updateQuantity(item.id, 1)}
                            data-testid={`increase-${item.id}`}
                          />
                        </div>

                        {/* Subtotal and Remove */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ color: textPrimary, fontSize: '1.125rem', fontWeight: 600 }}>
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                          <Button
                            kind="danger--ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={TrashCan}
                            iconDescription="Remove"
                            onClick={() => removeFromCart(item.id)}
                            data-testid={`remove-${item.id}`}
                          />
                        </div>
                      </div>
                    </div>
                  </Tile>
                ))}
              </Column>

              {/* Order Summary */}
              <Column lg={5} md={8} sm={4}>
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    padding: '20px',
                    position: 'sticky',
                    top: '64px',
                  }}
                >
                  <h3 style={{ color: textPrimary, fontSize: '1.125rem', fontWeight: 600, marginBottom: '20px' }}>
                    Order Summary
                  </h3>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Subtotal</span>
                      <span style={{ color: textPrimary, fontSize: '0.875rem' }}>${subtotal.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Shipping</span>
                      <span style={{ color: shipping === 0 ? '#24a148' : textPrimary, fontSize: '0.875rem' }}>
                        {shipping === 0 ? 'Included' : `$${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: textSecondary, fontSize: '0.875rem' }}>GST (10%)</span>
                      <span style={{ color: textPrimary, fontSize: '0.875rem' }}>${tax.toFixed(2)}</span>
                    </div>
                  </div>

                  <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600 }}>Total</span>
                      <span style={{ color: '#0f62fe', fontSize: '1.5rem', fontWeight: 600 }}>${total.toFixed(2)}</span>
                    </div>
                  </div>

                  {subtotal < 200 && (
                    <p style={{
                      color: textSecondary,
                      fontSize: '0.75rem',
                      marginBottom: '16px',
                      textAlign: 'center',
                      backgroundColor: bgPrimary,
                      padding: '8px',
                      borderRadius: '4px',
                    }}>
                      Add ${(200 - subtotal).toFixed(2)} more for free shipping!
                    </p>
                  )}

                  <Button
                    kind="primary"
                    style={{ width: '100%' }}
                    renderIcon={ArrowRight}
                    onClick={() => navigate('/checkout/shipping')}
                    data-testid="proceed-to-checkout"
                  >
                    Proceed to Shipping
                  </Button>
                </Tile>
              </Column>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default CartPage;
